import dynamic from 'next/dynamic';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { S } from './styles';
import type { StyliticsRecommendations as StyliticsRecommendationsProps } from '~/amplienceTypes/schemas/imported/stylitics-recommendations-schema';
import { QuickView } from '~/components/shared/quickView/QuickView';
import {
  CONTAINER_ID,
  SHOP_THE_MODEL_SELECTOR,
  BADGE_MOUNT_ATTRIBUTE,
} from '~/components/shared/recommendations/stylitics/constants';
import { resetJumpLinkState } from '~/components/shared/recommendations/stylitics/jumpLinkStore';
import type {
  StyliticsClickItemData,
  StyliticsMountOutfitData,
} from '~/components/shared/recommendations/stylitics/types';
import { useStyliticsComposer } from '~/components/shared/recommendations/stylitics/useStyliticsComposer';
import { useStyliticsConfiguration } from '~/components/shared/recommendations/stylitics/useStyliticsConfiguration';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { getProductBaseCode } from '~/utilities/helpers/getProductBaseCode';

const ShopTheModelBadge = dynamic(
  async () =>
    (await import('~/components/shared/recommendations/stylitics/ShopTheModelBadge'))
      .ShopTheModelBadge,
  { ssr: false }
);

export const StyliticsRecommendations = memo(
  ({ title, jumpLink, shopTheModel, shopTheModelIconEnabled }: StyliticsRecommendationsProps) => {
    const quickViewRef = useRef<{
      itemId: string | undefined;
      outfitId: string | undefined;
    } | null>(null);
    const { quickViewContent } = useStaticContext();
    const { product } = usePDPContext();

    const [quickViewCode, setQuickViewCode] = useState<string | undefined>();
    const [badgeMountPoint, setBadgeMountPoint] = useState<HTMLElement | null>(null);

    const basePriceColor = quickViewContent?.productPrice?.content?.basePriceColor;

    const productCode = useMemo(() => getProductBaseCode(product), [product]);

    const onMountOutfit = useCallback(
      ({ element, outfit }: StyliticsMountOutfitData) => {
        if (!shopTheModel || !outfit['on-model-image']) {
          return;
        }

        const container = element.querySelector<HTMLElement>(SHOP_THE_MODEL_SELECTOR);

        if (!container) {
          return;
        }

        // The badge is rendered into a node we own, so Stylitics rebuilding its
        // own DOM can never invalidate the children React manages.
        const existingMountPoint = container.querySelector<HTMLElement>(
          `[${BADGE_MOUNT_ATTRIBUTE}]`
        );
        const mountPoint = existingMountPoint ?? document.createElement('div');

        if (!existingMountPoint) {
          mountPoint.setAttribute(BADGE_MOUNT_ATTRIBUTE, '');
          container.appendChild(mountPoint);
        }

        setBadgeMountPoint(mountPoint);
      },
      [shopTheModel]
    );

    const onClickItem = useCallback((data: StyliticsClickItemData) => {
      quickViewRef.current = {
        itemId: data?.item.remote_id,
        outfitId: data?.outfit.id,
      };
      setQuickViewCode(data?.item.remote_id);
    }, []);

    const configuration = useStyliticsConfiguration(
      productCode,
      onMountOutfit,
      onClickItem,
      title,
      jumpLink
    );

    const composer = useStyliticsComposer(productCode, configuration);

    // The store outlives this page; reset it so the next PDP's JumpLink starts hidden.
    useEffect(() => () => resetJumpLinkState(), []);

    const onClose = useCallback(() => {
      setQuickViewCode(undefined);
      quickViewRef.current = null;
    }, []);

    const onOpen = useCallback(() => {
      const { itemId, outfitId } = quickViewRef?.current ?? {};

      composer?.modules?.[0].trackViewItem(itemId, {
        outfitId,
        itemImageType: 'client_original_image',
      });
    }, [composer]);

    const onQuickViewItemNavigation = useCallback(
      (code: string) => () => {
        const { outfitId } = quickViewRef?.current ?? {};

        composer?.modules[0].trackClickItem(code, {
          outfitId,
          uiComponent: 'widget',
        });
      },
      [composer]
    );

    return (
      <S.RecommendationsWrapper $discountColor={basePriceColor}>
        <S.QuickViewWrapper>
          <QuickView
            code={quickViewCode}
            onClose={onClose}
            onViewDetails={onQuickViewItemNavigation}
            onOpen={onOpen}
            algoliaAddToCartEventName=""
            algoliaAddToCartAfterSearchEventName=""
            placement="widget"
            autoOpenOnCodeChange
          />
        </S.QuickViewWrapper>
        <S.RecommendationsContainer id={CONTAINER_ID} />
        {badgeMountPoint &&
          shopTheModel &&
          createPortal(
            <ShopTheModelBadge text={shopTheModel} showIcon={shopTheModelIconEnabled} />,
            badgeMountPoint
          )}
      </S.RecommendationsWrapper>
    );
  }
);

StyliticsRecommendations.displayName = 'StyliticsRecommendations';
